import { ConnectorCategory } from '../../types/connector';
import { EDGE_DESCRIPTION, EDGE_DISPLAY_NAME, EDGE_TYPE } from './edgeService';

export interface ConnectorTypeDefinition {
  type: string;
  displayName: string;
  description: string;
  category: ConnectorCategory;
  iconName: string;
  color: string;
}

// Concrete database engines. The "PostgreSQL / MySQL / MSSQL" tile is a
// category, never a stored type — resolveDbType turns it into one of these.
export const DB_TYPES = new Set(['POSTGRESQL', 'MYSQL', 'SQLSERVER']);

const DB_URL_SCHEMES: Record<string, string> = {
  postgresql: 'POSTGRESQL',
  postgres: 'POSTGRESQL',
  mysql: 'MYSQL',
  sqlserver: 'SQLSERVER',
  mssql: 'SQLSERVER',
};

/**
 * Derive the concrete database type from the connection URL's scheme.
 *
 * Storing the generic RELATIONAL_DB instead would route the connector through
 * the REST code path and fail at query time, so an unrecognised scheme is worth
 * leaving as-is rather than guessing an engine.
 */
export function resolveDbType(selectedType: string, baseUrl: string | undefined): string {
  if (selectedType !== 'RELATIONAL_DB' || !baseUrl) return selectedType;
  const scheme = baseUrl.trim().toLowerCase().split('://')[0];
  return DB_URL_SCHEMES[scheme] ?? selectedType;
}

export const CONNECTOR_TYPES: ConnectorTypeDefinition[] = [
  {
    type: 'REST_API',
    displayName: 'REST API',
    description: 'Connect to any REST API endpoint with full auth support',
    category: 'REST',
    iconName: 'Globe',
    color: '#1D4ED8',
  },
  {
    type: 'GRAPHQL_API',
    displayName: 'GraphQL API',
    description: 'Query GraphQL endpoints with schema introspection',
    category: 'GraphQL',
    iconName: 'Code2',
    color: '#6D28D9',
  },
  {
    type: 'WEBSOCKET',
    displayName: 'WebSocket',
    description: 'Real-time streaming data via WebSocket connections',
    category: 'Stream',
    iconName: 'Zap',
    color: '#059669',
  },
  {
    type: 'SAP_ERP',
    displayName: 'SAP ECC/S4HANA',
    description: 'Extract data from SAP ERP systems via RFC or OData',
    category: 'ERP',
    iconName: 'Building2',
    color: '#92400E',
  },
  {
    type: 'SAP_B1',
    displayName: 'SAP Business One',
    description: 'Read sales, purchasing, BP, and item data from SAP Business One (v10) via the Service Layer REST API. Cookie-session auth with CompanyDB + user; pick the entity set (Orders, Invoices, BusinessPartners, Items, …).',
    category: 'ERP',
    iconName: 'Building2',
    color: '#0A6ED1',
  },
  {
    type: 'HUBSPOT',
    displayName: 'HubSpot',
    description: 'Sync contacts, companies, deals, and tickets from HubSpot CRM v3 via a private-app token. Cursor pagination and incremental sync on lastmodifieddate, driven by the bundled extraction recipe.',
    category: 'CRM',
    iconName: 'Users',
    color: '#7E22CE',
  },
  {
    type: 'SALESFORCE',
    displayName: 'Salesforce',
    description: 'Read any standard or custom object via the REST query API — the bundled recipe builds the SOQL, follows nextRecordsUrl pagination, and syncs incrementally on LastModifiedDate. Connected-app OAuth with refresh token.',
    category: 'CRM',
    iconName: 'Cloud',
    color: '#0EA5E9',
  },
  {
    type: 'RELATIONAL_DB',
    displayName: 'PostgreSQL / MySQL / MSSQL',
    description: 'Direct database connections with CDC and batch modes',
    category: 'DB',
    iconName: 'Database',
    color: '#166534',
  },
  {
    type: 'FILE_UPLOAD',
    displayName: 'File Upload CSV/XLSX/JSON',
    description: 'Upload and parse structured file formats',
    category: 'File',
    iconName: 'FileText',
    color: '#0C4A6E',
  },
  {
    type: 'OBJECT_STORAGE',
    displayName: 'Amazon S3 / almacenamiento de objetos',
    description: 'Explore a bucket in Amazon S3, MinIO, Google Cloud Storage (HMAC keys) or any S3-compatible service: browse prefixes, preview CSV, XLSX, JSON and Parquet files with header, delimiter and schema options, and read the files a glob pattern selects from a pipeline source, optionally only the new or changed ones. Access key and secret or an IAM role; credentials encrypted at rest.',
    category: 'File',
    iconName: 'Cloud',
    color: '#C2410C',
  },
  {
    type: 'DOCUMENT_UPLOAD',
    displayName: 'Document Upload PDF/DOCX',
    description: 'Extract and structure content from documents using AI',
    category: 'Doc',
    iconName: 'FileSearch',
    color: '#713F12',
  },
  {
    type: 'GOOGLE_SHEETS',
    displayName: 'Google Sheets',
    description: 'Pull a sheet range as rows — the first row names the columns. API-key auth reads sheets shared by link; paste the spreadsheet ID and go.',
    category: 'File',
    iconName: 'Table',
    color: '#166534',
  },
  {
    type: 'KAFKA',
    displayName: 'Kafka / Redpanda',
    description: 'High-throughput streaming from Kafka or Redpanda topics',
    category: 'Stream',
    iconName: 'Activity',
    color: '#065F46',
  },
  {
    type: 'WEBHOOK',
    displayName: 'Custom Webhook',
    description: 'Receive push events from any system via HTTP webhook',
    category: 'HTTP',
    iconName: 'Webhook',
    color: '#9F1239',
  },
  {
    type: 'MONGODB',
    displayName: 'MongoDB',
    description: 'Connect to MongoDB collections with change stream support',
    category: 'DB',
    iconName: 'Layers',
    color: '#166534',
  },
  {
    type: 'DATA_WAREHOUSE',
    displayName: 'Snowflake / BigQuery',
    description: 'Query cloud data warehouses for analytics pipelines',
    category: 'DW',
    iconName: 'Snowflake',
    color: '#1E293B',
  },
  {
    type: 'POSTMAN',
    displayName: 'Postman Collection',
    description: 'Import a Postman collection JSON — drag and drop to auto-configure',
    category: 'REST',
    iconName: 'PackageOpen',
    color: '#EF6C1A',
  },
  {
    type: 'FIREFLIES',
    displayName: 'Fireflies.ai',
    description: 'Sync meeting transcripts, summaries, and action items from Fireflies',
    category: 'Productivity',
    iconName: 'Mic',
    color: '#7C3AED',
  },
  // Two different products are called "WhatsApp" (ADR-10). This one is the
  // linked-device/QR connector; the label says so because the Cloud API card
  // below is the one an operator wants for a business number. The entry is
  // dormant, never deleted: removing it would orphan ConnectorGrid's setup
  // branch, ConnectorDetailPanel's WhatsApp tab and the live
  // /{connector_id}/whatsapp/* proxy.
  {
    type: 'WHATSAPP',
    displayName: 'WhatsApp (QR — dispositivo vinculado)',
    description: 'Monitor WhatsApp chats and groups via linked device — scan QR to connect',
    category: 'Messaging',
    iconName: 'MessageCircle',
    color: '#25D366',
  },
  {
    type: 'WHATSAPP_CLOUD',
    displayName: 'WhatsApp Cloud API',
    description: 'Conecta un número de WhatsApp Business con la Cloud API de Meta. Un número por conector: recibe y envía mensajes, y eliges quién responde en él. Si el asesor sigue atendiendo desde la app de WhatsApp Business en el teléfono, elige coexistencia al registrarlo.',
    category: 'Messaging',
    iconName: 'MessageCircle',
    color: '#128C7E',
  },
  // A Page and an Instagram account connect through the same dialog
  // (MetaPagesSetupModal): one Facebook Login reaches both.
  {
    type: 'FACEBOOK_PAGE',
    displayName: 'Facebook Messenger',
    description: 'Conecta páginas de Facebook para responder por Messenger. Inicias sesión con Facebook, eliges las páginas y el bot que responde en ellas.',
    category: 'Messaging',
    iconName: 'MessageCircle',
    color: '#0866FF',
  },
  {
    type: 'INSTAGRAM_ACCOUNT',
    displayName: 'Instagram',
    description: 'Conecta cuentas profesionales de Instagram para responder mensajes directos. Usa la página de Facebook vinculada o inicia sesión con Instagram, y elige el bot que responde.',
    category: 'Messaging',
    iconName: 'MessageCircle',
    color: '#E1306C',
  },
  {
    type: 'SLACK',
    displayName: 'Slack',
    description: 'Pull channel messages and threads from a Slack workspace via a bot token — one row per message',
    category: 'Messaging',
    iconName: 'Hash',
    color: '#4A154B',
  },
  {
    type: 'TEAMS',
    displayName: 'Microsoft Teams',
    description: 'Pull channel messages and replies from Microsoft Teams via the Graph API — per-tenant Azure AD app with application permissions',
    category: 'Messaging',
    iconName: 'MessagesSquare',
    color: '#6264A7',
  },
  {
    type: 'JIRA',
    displayName: 'Jira',
    description: 'Pull issues and their full changelog from Jira Cloud, flattened into process-mining event rows (case, activity, timestamp, user). Basic auth with your Atlassian email + API token; scope by project key or custom JQL.',
    category: 'Productivity',
    iconName: 'Kanban',
    color: '#0052CC',
  },
  {
    type: 'GITHUB',
    displayName: 'GitHub',
    description: 'Pull commits, pull requests, reviews, and issues from GitHub repos or orgs',
    category: 'Productivity',
    iconName: 'GitBranch',
    color: '#24292F',
  },
  {
    type: 'EMAIL_INBOX',
    displayName: 'Email Inbox',
    description: 'Link a mailbox two ways: Microsoft 365 / Outlook via Graph API OAuth2 (delegated or unattended app-only — the only path that works for work accounts, since Exchange Online disabled IMAP app-passwords), or any IMAP host — Gmail, Yahoo, iCloud, Zoho, FastMail, custom — via app password. Same row shape either way.',
    category: 'Messaging',
    iconName: 'Mail',
    color: '#0EA5E9',
  },
  {
    type: 'GRAFANA_INFLUX',
    displayName: 'Grafana / InfluxDB',
    description: 'Pull time-series data from an InfluxDB datasource via Grafana\'s query proxy. Auth uses a Grafana API key (Viewer role is enough). SOURCE nodes specify measurement, field, and an optional device list.',
    category: 'Stream',
    iconName: 'Activity',
    color: '#F46800',
  },
  {
    type: 'SHAREPOINT',
    displayName: 'SharePoint',
    description: 'Browse and sync files from Microsoft SharePoint document libraries via Graph API. Per-tenant Azure AD app — your Nexus tenant supplies its own client_id/secret. OAuth2 + refresh tokens.',
    category: 'Doc',
    iconName: 'FolderTree',
    color: '#0078D4',
  },
  {
    type: 'LASERFICHE',
    displayName: 'Laserfiche',
    description: 'Walk a Laserfiche repository and pull documents (and optionally folders) as flat rows with template-field metadata. Self-hosted API Server via username/password, or Laserfiche Cloud via a service app Access Key.',
    category: 'Doc',
    iconName: 'Archive',
    color: '#E35205',
  },
  {
    type: 'DROPBOX',
    displayName: 'Dropbox',
    description: 'Browse, sync and download files from a Dropbox account or Business team space. Scope the connector to the entire Dropbox, one folder, or a shared link; filter by extension, depth and excluded paths; delta sync via Dropbox cursors. Per-tenant Dropbox app (app key/secret) with OAuth2 refresh tokens, or a pasted token.',
    category: 'Doc',
    iconName: 'Box',
    color: '#0061FF',
  },
  {
    type: 'PDF_EXTRACTOR',
    displayName: 'PDF Extractor',
    description: 'Upload a PDF and run a vision LLM page-by-page to extract structured rows. Matches images to products. Optional one-click push to an ontology object type.',
    category: 'Doc',
    iconName: 'FileText',
    color: '#7C3AED',
  },
  // An agent the operator installs on their OWN server: it watches a folder and
  // pushes to PAI, so there is nothing for PAI to connect INTO and nothing for
  // the operator to type but a name. The token PAI mints is the credential.
  {
    type: EDGE_TYPE,
    displayName: EDGE_DISPLAY_NAME,
    description: EDGE_DESCRIPTION,
    category: 'File',
    iconName: 'PackageOpen',
    color: '#0F766E',
  },
  {
    type: 'META_SCRAPER',
    displayName: 'Meta Scraper (FB / IG)',
    description: 'Scrape public Facebook Pages and Instagram profiles via Apify. Ingests posts, captions, comments, and engagement; downstream VISION_EXTRACT pulls price/product details from images. Fragile and against Meta ToS — best-effort reliability, surface that to operators.',
    category: 'Messaging',
    iconName: 'Share2',
    color: '#E1306C',
  },
];

export const getConnectorTypeDef = (type: string): ConnectorTypeDefinition | undefined =>
  CONNECTOR_TYPES.find((c) => c.type === type);
